/**
 * Der Zustand eines Entwicklers — für ALLE drei dieselben Worte. (18.10.2026)
 *
 * Martin: „In der Übersicht steht ‚Schmied hört zu‘, bei Zimmermann nur ‚bereit‘. Was ist der Unterschied?“
 * Der Unterschied war keiner der Sache, sondern der Quelle: beim Schmied zeigte die Übersicht den LIVE-Zustand (Hörer verbunden),
 * beim Zimmermann/Hüter nur „Notschalter + Zugang eingerichtet“. Jetzt rechnen Übersicht, Lage-Karte und die Seite „Entwickler“
 * mit EINER Regel:
 *
 *   aus       Hauptschalter „Entwickler“ aus, oder sein Kontoschalter aus (bei Zimmermann/Hüter auch: „Entwickler im Chat“ aus)
 *   gesperrt  der Server lässt ihn nicht herein (Notschalter oder Zugang fehlt)
 *   bereit    eingeschaltet und zugelassen, kein Hörer — aber der letzte Kontakt ist jüngst (ein Wiederverbinden)
 *   hört zu   ein Hörer ist verbunden
 *   nicht verbunden  eingeschaltet und zugelassen, aber seit über 2 Minuten kein Hörer (Warnung; „zuletzt vor 25 Min.“)
 *   unbekannt der Stand am Server ist nicht abrufbar
 *
 * Nur Booleans — nie ein Inhalt. Rein.
 */
import { ZUSATZ_IDS, entwickler, type EntwicklerId } from "../../../shared/entwickler.js";

export type EntwicklerZustand = "aus" | "gesperrt" | "bereit" | "hoert" | "getrennt" | "unbekannt";

/** Ab so vielen Sekunden ohne Hörer gilt „nicht verbunden“ (wie GETRENNT_NACH_S am Server). */
export const GETRENNT_NACH_S = 120;

/** Das Wort — immer dasselbe, in der Übersicht, der Karte und auf der Seite. */
export const ZUSTAND_WORT: Readonly<Record<EntwicklerZustand, string>> = {
  aus: "aus",
  gesperrt: "gesperrt",
  bereit: "bereit",
  hoert: "hört zu",
  getrennt: "nicht verbunden",
  unbekannt: "unbekannt",
};

/** Das Zeichen daneben (nie nur Farbe): – aus, × gesperrt, ✓ bereit, ● hört zu, ? unbekannt. */
export const ZUSTAND_ZEICHEN: Readonly<Record<EntwicklerZustand, string>> = {
  aus: "–",
  gesperrt: "×",
  bereit: "✓",
  hoert: "●",
  getrennt: "!",
  unbekannt: "?",
};

export interface ZustandsEingabe {
  /** Der Kontoschalter dieses Entwicklers (und bei Zimmermann/Hüter: „Entwickler im Chat“) ist an. */
  an: boolean;
  /** Notschalter oder Zugang fehlt am Server; `null`: unbekannt. Der Schmied kennt keine Sperre (`false`). */
  gesperrt: boolean | null;
  /** Ein Hörer ist verbunden; `null`: der Server sagt es nicht (älterer Stand). */
  hoert: boolean | null;
  /** Sekunden seit dem letzten Hörer-Kontakt; `null`: seit dem Start nie; fehlt: der Server sagt es nicht (älterer Stand). */
  zuletztS?: number | null;
}

export function entwicklerZustand(e: ZustandsEingabe): EntwicklerZustand {
  if (!e.an) return "aus";
  if (e.gesperrt === null) return "unbekannt";
  if (e.gesperrt) return "gesperrt";
  if (e.hoert === true) return "hoert";
  // Kein Hörer: bei einer Lücke über 2 Minuten (oder nie gesehen) eine Warnung — sonst nur ein Wiederverbinden.
  if (e.hoert === false && e.zuletztS !== undefined && (e.zuletztS === null || e.zuletztS > GETRENNT_NACH_S)) return "getrennt";
  return "bereit";
}

/** Das Zeichen als Element (`.einst-lage-zeichen`, die Farbe kommt aus `data-art`), für Karte und Seite gleich. */
export function zustandsZeichen(z: EntwicklerZustand): HTMLElement {
  const zeichen = document.createElement("span");
  zeichen.className = "einst-lage-zeichen";
  zeichen.dataset.art = z;
  zeichen.setAttribute("aria-hidden", "true");
  zeichen.textContent = ZUSTAND_ZEICHEN[z];
  return zeichen;
}

/** Die Rolle in einem Wort. */
export const ROLLE: Readonly<Record<EntwicklerId, string>> = {
  schmied: "Bau",
  zimmermann: "Panel",
  hueter: "Sicherung",
};

export const TEAM_IDS: readonly EntwicklerId[] = ["schmied", ...ZUSATZ_IDS];

/** Der Anzeigename (aus shared/entwickler.ts — ein Ort). */
export const teamName = (id: EntwicklerId): string => entwickler(id).name;

/** „zuletzt vor 25 Min.“ — grob und ruhig; `null` (seit dem Start nie): „seit dem Start nicht gesehen“. Rein. */
export function zuletztText(s: number | null | undefined): string {
  if (s === undefined) return "";
  if (s === null) return "seit dem Start nicht gesehen";
  const min = Math.round(s / 60);
  if (min < 1) return "zuletzt vor wenigen Sekunden";
  if (min < 60) return `zuletzt vor ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 48) return `zuletzt vor ${std} Std.`;
  return `zuletzt vor ${Math.round(std / 24)} Tagen`;
}
